import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
  Menu,
  Bell,
  ChevronDown,
  LogOut,
  User,
  LayoutDashboard,
  Package,
  KeyRound,
  ClipboardList,
  FileTextIcon,
  Settings,
  Users,
  Cloud,
  User2,
  Cpu,
} from "lucide-react";
import { BRAND } from "../css/brand";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import profileImage from "../asset/images/favi.jpg";
import Dashboards from "../Dashboard/Dashboards";
import Entities from "../Dashboard/Entities";
import Keys from "../Dashboard/Keys";
import Inventory from "../Dashboard/Inventory";
import Orders from "../Dashboard/Orders";
import Payments from "../Dashboard/Payments";
import Balances from "../Dashboard/Balance";
import Support from "../Dashboard/Support";
import Account from "../Dashboard/Account";
import ActivationReport from "../Dashboard/ActivationReport";
import UserViewPage from "../Dashboard/UserViewPage";
import EnterpriseUI from "../Dashboard/EnterpriseUI";
import FormWizardTesting from "../Components/FormWizardTesting";
import Devices from "../Dashboard/Devices";
import CustomerCards from "../Components/CustomerCard";
import DeletedCustomers from "../Dashboard/DeletedCustomers";

export default function MultiRolePanel() {
  const [role, setRole] = useState(null);
  const [businessName, setBusinessName] = useState("");
  const [code, setCode] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 640);
  const [open, setOpen] = useState(false);
  const [deviceCount, setDeviceCount] = useState(0);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Define which pages each role can see
  const roleRoutes = {
    2: [ // Company
      "dashboard", "entities", "keys", "inventory", "report", "customers",
      "recover", "devices", "enterprises", "orders", "account"
    ],
    3: [ // CNF
      "dashboard", "entities", "keys", "inventory", "customers", "orders", "account"
    ],
    4: [ // SuperStockist
      "dashboard", "entities", "keys", "inventory", "customers", "orders", "account"
    ],
    5: [ // Distributor
      "dashboard", "entities", "keys", "inventory", "customers", "orders", "account"
    ],
    6: [ // Retailer
      "dashboard","entities", "keys", "inventory", "customers", "account"
    ],
  };

  // ✅ Sidebar menu list
  const nav = [
    { path: "/dashboard", icon: <LayoutDashboard size={18} />, label: "Dashboard" },
    { path: "/dashboard/entities", icon: <Users size={18} />, label: "Entities" },
    { path: "/dashboard/keys", icon: <KeyRound size={18} />, label: "Keys" },
    { path: "/dashboard/inventory", icon: <Package size={18} />, label: "Inventory" },
    { path: "/dashboard/report", icon: <FileTextIcon size={18} />, label: "Insights" },
    { path: "/dashboard/customers", icon: <User2 size={18} />, label: "Customers" },
    { path: "/dashboard/recover", icon: <User2 size={18} />, label: "Recover" },
    { path: "/dashboard/devices", icon: <Cpu size={18} />, label: "Devices" },
    { path: "/dashboard/enterprises", icon: <Cloud size={18} />, label: "Enterprises" },
    { path: "/dashboard/orders", icon: <ClipboardList size={18} />, label: "Orders" },
    { path: "/dashboard/account", icon: <Settings size={18} />, label: "Account" },
  ];

  // ✅ Session handling
  useEffect(() => {
    const stored = sessionStorage.getItem("authUserData1");
    if (stored) {
      try {
        const user = JSON.parse(stored);
        const u = user?.data?.[0];
        if (u) {
          setRole(u.role);
          setBusinessName(u.business_name || u.owner_name || "User");
          setCode(u.org_code || "-");
        }
      } catch (err) {
        console.error("Invalid session data:", err);
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  // ✅ Fetch device count from API (Company only)
  useEffect(() => {
    if (role !== 2) return;

    const fetchDeviceCount = async () => {
      try {
        const res = await axios.get("http://spillas.com/api/v1/ama-enterprises?per_page=100000");
        if (res.data && Array.isArray(res.data.data)) {
          const defaultEnterprise = res.data.data.find((item) => item.default_enterprise === 1);
          const count = defaultEnterprise?.device_count || 0;
          setDeviceCount(count);
        }
      } catch (err) {
        console.error("Error fetching device count:", err);
      }
    };

    fetchDeviceCount();
    const interval = setInterval(fetchDeviceCount, 60000);
    return () => clearInterval(interval);
  }, [role]);

  // ✅ Auto-close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Redirect unauthorized routes
  const currentPage = location.pathname.split("/")[2] || "dashboard";
  useEffect(() => {
    if (role && !roleRoutes[role]?.includes(currentPage)) {
      navigate("/dashboard");
    }
  }, [role, currentPage, navigate]);

  // ✅ Handle logout & profile
  const handleLogout = () => {
    sessionStorage.removeItem("authUserData1");
    navigate("/login");
  };

  const handleProfileClick = () => {
    navigate("/dashboard/account");
    setOpen(false);
  };

  // ✅ Page components mapping
  const pageComponents = {
    dashboard: <Dashboards role={role} />,
    entities: <Entities role={role} />,
    keys: <Keys role={role} />,
    inventory: <Inventory />,
    report: <ActivationReport />,
    customers: <CustomerCards />,
    recover: <DeletedCustomers />,
    devices: <Devices />,
    orders: <Orders />,
    payments: <Payments />,
    balances: <Balances />,
    support: <Support />,
    account: <Account />,
    enterprises: <EnterpriseUI />,
  };

  if (!role) return null; // Wait for role load

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-40 shadow-sm" style={{ backgroundColor: BRAND.darkBlue }}>
        <div className="mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex h-14 items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSidebarOpen((s) => !s)}
                className="hidden sm:inline-flex rounded-xl p-2 hover:bg-white/10"
              >
                <Menu className="text-white" size={20} />
              </button>
              <span className="hidden sm:inline-flex text-sm font-semibold tracking-wide text-white/80">
                {businessName}
              </span>
              <img
                src={profileImage}
                alt="Profile"
                className="inline-flex lg:hidden md:hidden rounded-lg h-12 w-12 object-cover border border-white/30"
              />

            </div>

            <div className="flex items-center gap-3">
              <div className="flex flex-row gap-2">
              {role === 2 && (
                <div className="w-auto px-3 py-1 rounded-full border border-blue-200 bg-blue-50 text-sm text-blue-700 font-medium shadow-sm">
                  <span className="hidden sm:inline">Count:&nbsp;</span>
                  <span className="font-bold text-blue-700">{deviceCount || 0}</span>
                </div>
              )}
              </div>

              {/* <button className="relative rounded-xl p-2 hover:bg-white/10">
                <Bell className="text-white" size={18} />
              </button> */}

              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setOpen(!open)}
                  className="group flex items-center gap-2 rounded-xl bg-white/10 px-2 py-1.5 text-white hover:bg-white/20"
                >
                  <img
                    src={profileImage}
                    alt="Profile"
                    className="hidden sm:inline-flex h-6 w-6 rounded-full object-cover border border-white/30"
                  />
                  <span className="text-sm">{code}</span>
                  <ChevronDown size={16} className="opacity-80" />
                </button>

                {open && (
                  <div className="absolute right-0 mt-2 w-40 rounded-xl bg-white shadow-lg ring-1 ring-black/5 z-50">
                    <ul className="py-1 text-sm text-gray-700">
                      <li>
                        <button
                          onClick={handleProfileClick}
                          className="flex w-full items-center gap-2 px-4 py-2 hover:bg-gray-100"
                        >
                          <User size={16} /> Profile
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={handleLogout}
                          className="flex w-full items-center gap-2 px-4 py-2 hover:bg-gray-100 text-red-600"
                        >
                          <LogOut size={16} /> Logout
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main layout */}
      <div className="mx-auto px-3 sm:px-6 lg:px-8">
        <div className="grid grid-cols-12 gap-3 sm:gap-4 py-3 sm:py-4">
          {/* Sidebar */}
          {sidebarOpen && (
            <aside className="hidden sm:block col-span-12 sm:col-span-4 lg:col-span-2">
              <div className="sticky top-16 space-y-2 rounded-2xl p-2 bg-slate-50">
                {nav
                  .filter((n) => {
                    const segment = n.path.split("/")[2] || "dashboard";
                    return roleRoutes[role]?.includes(segment);
                  })
                  .map((n) => (
                    <NavLink
                      key={n.path}
                      to={n.path}
                      end={n.path === "/dashboard"}
                      className={({ isActive }) =>
                        `flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm ${
                          isActive ? "bg-white font-semibold" : "hover:bg-white"
                        }`
                      }
                    >
                      <span className="text-slate-700">{n.icon}</span>
                      <span className="text-slate-700">{n.label}</span>
                    </NavLink>
                  ))}
              </div>
            </aside>

          )}

          {/* Main content */}
          <main
            className={
              sidebarOpen ? "col-span-12 sm:col-span-8 lg:col-span-10" : "col-span-12"
            }
          >
            {location.pathname.startsWith("/dashboard/entity/") ? (
              <UserViewPage />
            ) : location.pathname === "/dashboard/enterprises/create" ? (
              <FormWizardTesting />
            ) : (
              pageComponents[currentPage]
            )}
          </main>

          <nav className="fixed bottom-0 left-0 right-0 z-50 overflow-x-auto bg-white border-t shadow sm:hidden">
              <div className="flex w-max">
                {nav.map((n) => (
                  <NavLink
                    key={n.path}
                    to={n.path}
                    end={n.path === "/dashboard"}
                    className={({ isActive }) =>
                      `flex-shrink-0 w-20 flex flex-col items-center justify-center py-2 text-xs ${
                        isActive ? "text-blue-600" : "text-slate-600"
                      }`
                    }
                  >
                    {n.icon}
                    <span>{n.label}</span>
                  </NavLink>
                ))}
              </div>
            </nav>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-4 sm:mt-6 border-t py-4 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} RR Technology
      </footer>
    </div>
  );
}


// import React, { useEffect, useState, useRef } from "react";
// import axios from "axios";
// import {
//   Menu,
//   Bell,
//   ChevronDown,
//   LogOut,
//   User,
//   LayoutDashboard,
//   Package,
//   KeyRound,
//   ClipboardList,
//   FileText,
//   Settings,
//   BarChart3,
//   Users,
//   FileTextIcon,
//   Cloud,
//   User2,
//   Cpu,
// } from "lucide-react";
// import { BRAND } from "../css/brand";
// import { useNavigate, useLocation, NavLink } from "react-router-dom";
// import profileImage from "../asset/images/favi.jpg";
// import Sidebar from "../Dashboard/Sidebar";
// import Dashboards from "../Dashboard/Dashboards";
// import Entities from "../Dashboard/Entities";
// import Keys from "../Dashboard/Keys";
// import Inventory from "../Dashboard/Inventory";
// import Orders from "../Dashboard/Orders";
// import Payments from "../Dashboard/Payments";
// import Balances from "../Dashboard/Balance";
// import Support from "../Dashboard/Support";
// import Account from "../Dashboard/Account";
// import ActivationReport from "../Dashboard/ActivationReport";
// import UserViewPage from "../Dashboard/UserViewPage";
// import Enterprise from "../Dashboard/Enterprise";
// import EnterpriseUI from "../Dashboard/EnterpriseUI";
// import FormWizardTesting from "../Components/FormWizardTesting";
// import Devices from "../Dashboard/Devices";
// import CustomerCards from "../Components/CustomerCard";
// import DeletedCustomers from "../Dashboard/DeletedCustomers";

// export default function MultiRolePanel() {
//   const [role, setRole] = useState(2);
//   const [businessName, setBusinessName] = useState("");
//   const [code, setCode] = useState("");
//   const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 640);
//   const [open, setOpen] = useState(false);
//   const [deviceCount, setDeviceCount] = useState(0); // ✅ new state for badge count
//   console.log("count: ", deviceCount)
//   const menuRef = useRef(null);
//   const navigate = useNavigate();
//   const location = useLocation();

//   // ✅ Pages mapping
//   const pageComponents = {
//     dashboard: <Dashboards role={role} />,
//     entities: <Entities role={role} />,
//     keys: <Keys role={role} />,
//     inventory: <Inventory />,
//     report: <ActivationReport />,
//     customers: <CustomerCards />,
//     recover: <DeletedCustomers/>,
//     devices: <Devices />,
//     orders: <Orders />,
//     payments: <Payments />,
//     balances: <Balances />,
//     support: <Support />,
//     account: <Account />,
//     enterprises: <EnterpriseUI />,
//   };

//   const nav = [
//     { path: "/dashboard", icon: <LayoutDashboard size={18} />, label: "Dashboard" },
//     { path: "/dashboard/entities", icon: <Users size={18} />, label: "Entities" },
//     { path: "/dashboard/keys", icon: <KeyRound size={18} />, label: "Keys" },
//     { path: "/dashboard/inventory", icon: <Package size={18} />, label: "Inventory" },
//     { path: "/dashboard/report", icon: <FileTextIcon size={18} />, label: "Insights" },
//     { path: "/dashboard/customers", icon: <User2 size={18} />, label: "Customers" },
//     { path: "/dashboard/recover", icon: <User2 size={18} />, label: "Recover" },
//     { path: "/dashboard/devices", icon: <Cpu size={18} />, label: "Devices" },
//     { path: "/dashboard/enterprises", icon: <Cloud size={18} />, label: "Enterprises" },
//     { path: "/dashboard/orders", icon: <ClipboardList size={18} />, label: "Orders" },
//     { path: "/dashboard/account", icon: <Settings size={18} />, label: "Account" },
//   ];

//   // ✅ Session handling
//   useEffect(() => {
//     const stored = sessionStorage.getItem("authUserData1");
//     if (stored) {
//       try {
//         const user = JSON.parse(stored);
//         const u = user?.data?.[0];
//         if (u) {
//           setRole(u.role);
//           setBusinessName(u.business_name || u.owner_name || "User");
//           setCode(u.org_code || "-");
//         }
//       } catch (err) {
//         console.error("Invalid session data:", err);
//       }
//     } else {
//       navigate("/login");
//     }
//   }, [navigate]);

//   // ✅ Fetch device count from API
//   useEffect(() => {
//   if (role !== 2) return; // Run only for Company users

//   const fetchDeviceCount = async () => {
//     try {
//       const res = await axios.get("http://spillas.com/api/v1/ama-enterprises?per_page=100000");

//       if (res.data && Array.isArray(res.data.data)) {
//         // Find the enterprise where default_enterprise === 1
//         const defaultEnterprise = res.data.data.find(
//           (item) => item.default_enterprise === 1
//         );

//         // Get device_count value (fallback 0)
//         const count = defaultEnterprise?.device_count || 0;
//         setDeviceCount(count);

//         // ✅ Toast warning if count exceeds 350
//         // if (count > 350) {
//         //   toast.error("⚠️ QR getting full — please update company QR.", {
//         //     duration: 5000,
//         //     position: "top-center",
//         //   });
//         // }
//       }
//     } catch (err) {
//       console.error("Error fetching device count:", err);
//     }
//   };

//   fetchDeviceCount();

//   // Optional: refresh every 1 minute
//   const interval = setInterval(fetchDeviceCount, 60000);
//   return () => clearInterval(interval);
// }, [role]);


//   // ✅ Auto-close dropdown when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (menuRef.current && !menuRef.current.contains(event.target)) {
//         setOpen(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   // ✅ Handle logout & profile
//   const handleLogout = () => {
//     sessionStorage.removeItem("authUserData1");
//     navigate("/login");
//   };

//   const handleProfileClick = () => {
//     navigate("/dashboard/account");
//     setOpen(false);
//   };

//   // ✅ Layout
//   return (
//     <div className="min-h-screen bg-white text-slate-900">
//       {/* Header */}
//       <header className="sticky top-0 z-40 shadow-sm" style={{ backgroundColor: BRAND.darkBlue }}>
//         <div className="mx-auto px-3 sm:px-6 lg:px-8">
//           <div className="flex h-14 items-center justify-between">
//             {/* Left: Menu + Business */}
//             <div className="flex items-center gap-2">
//               <button
//                 onClick={() => setSidebarOpen((s) => !s)}
//                 className="hidden sm:inline-flex rounded-xl p-2 hover:bg-white/10 focus:outline-none"
//               >
//                 <Menu className="text-white" size={20} />
//               </button>
//               <span className="text-sm font-semibold tracking-wide text-white/80">
//                 {businessName}
//               </span>
//             </div>

//             {/* Right: Notification + Profile */}
//             <div className="flex items-center gap-3">
//               {/* ✅ Company Device Count Pill */}
//               <div className="px-3 py-1 rounded-full border border-blue-200 bg-blue-50 text-sm text-blue-700 font-medium shadow-sm">
//                 {/* Label visible on medium+ screens */}
//                 <span className="hidden sm:inline">
//                   Company Device Count:&nbsp;
//                 </span>
//                 {/* Count always visible */}
//                 <span className="font-bold text-blue-700">{deviceCount || 0}</span>
//               </div>

//               {/* 🔔 Bell icon */}
//               <button className="relative rounded-xl p-2 hover:bg-white/10">
//                 <Bell className="text-white" size={18} />
//               </button>

//               {/* 👤 Profile dropdown */}
//               <div className="relative" ref={menuRef}>
//                 <button
//                   onClick={() => setOpen(!open)}
//                   className="group flex items-center gap-2 rounded-xl bg-white/10 px-2 py-1.5 text-white hover:bg-white/20 transition"
//                 >
//                   <img
//                     src={profileImage}
//                     alt="Profile"
//                     className="h-6 w-6 rounded-full object-cover border border-white/30"
//                   />
//                   <span className="text-sm">{code}</span>
//                   <ChevronDown size={16} className="opacity-80" />
//                 </button>

//                 {open && (
//                   <div className="absolute right-0 mt-2 w-40 rounded-xl bg-white shadow-lg ring-1 ring-black/5 z-50">
//                     <ul className="py-1 text-sm text-gray-700">
//                       <li>
//                         <button
//                           onClick={handleProfileClick}
//                           className="flex w-full items-center gap-2 px-4 py-2 hover:bg-gray-100"
//                         >
//                           <User size={16} /> Profile
//                         </button>
//                       </li>
//                       <li>
//                         <button
//                           onClick={handleLogout}
//                           className="flex w-full items-center gap-2 px-4 py-2 hover:bg-gray-100 text-red-600"
//                         >
//                           <LogOut size={16} /> Logout
//                         </button>
//                       </li>
//                     </ul>
//                   </div>
//                 )}
//               </div>
//             </div>

//           </div>
//         </div>
//       </header>

//       {/* Main Layout */}
//       <div className="mx-auto px-3 sm:px-6 lg:px-8">
//         <div className="grid grid-cols-12 gap-3 sm:gap-4 py-3 sm:py-4">
//           {/* Sidebar */}
//           {sidebarOpen && (
//             <aside className="hidden sm:block col-span-12 sm:col-span-4 lg:col-span-2">
//               <div className="sticky top-16 space-y-2 rounded-2xl p-2 bg-slate-50">
//                 {nav.map((n) => (
//                   <NavLink
//                     key={n.path}
//                     to={n.path}
//                     end={n.path === "/dashboard"}
//                     className={({ isActive }) =>
//                       `flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm ${
//                         isActive ? "bg-white font-semibold" : "hover:bg-white"
//                       }`
//                     }
//                   >
//                     <span className="text-slate-700">{n.icon}</span>
//                     <span className="text-slate-700">{n.label}</span>
//                   </NavLink>
//                 ))}
//               </div>
//             </aside>
//           )}

//           {/* Main Content */}
//           <main
//             className={
//               sidebarOpen ? "col-span-12 sm:col-span-8 lg:col-span-10" : "col-span-12"
//             }
//           >
//             {location.pathname.startsWith("/dashboard/entity/") ? (
//               <UserViewPage />
//             ) : location.pathname === "/dashboard/enterprises/create" ? (
//               <FormWizardTesting />
//             ) : (
//               pageComponents[location.pathname.split("/")[2] || "dashboard"]
//             )}
//           </main>
//         </div>
//       </div>

//       {/* Bottom Navigation (mobile) */}
      // <nav className="fixed bottom-0 left-0 right-0 z-50 overflow-x-auto bg-white border-t shadow sm:hidden">
      //   <div className="flex w-max">
      //     {nav.map((n) => (
      //       <NavLink
      //         key={n.path}
      //         to={n.path}
      //         end={n.path === "/dashboard"}
      //         className={({ isActive }) =>
      //           `flex-shrink-0 w-20 flex flex-col items-center justify-center py-2 text-xs ${
      //             isActive ? "text-blue-600" : "text-slate-600"
      //           }`
      //         }
      //       >
      //         {n.icon}
      //         <span>{n.label}</span>
      //       </NavLink>
      //     ))}
      //   </div>
      // </nav>

//       {/* Footer */}
//       <footer className="mt-4 sm:mt-6 border-t py-4 text-center text-xs text-slate-500">
//         © {new Date().getFullYear()} RR Technology — MultiRole Panel
//       </footer>
//     </div>
//   );
// }



// import React, { useEffect, useState, useRef } from "react";
// import {
//   Menu,
//   Bell,
//   ChevronDown,
//   LogOut,
//   User,
//   LayoutDashboard,
//   Package,
//   KeyRound,
//   ClipboardList,
//   FileText,
//   Settings,
//   BarChart3,
//   Users,
//   FileTextIcon,
//   Cloud,
//   User2,
//   Cpu,
// } from "lucide-react";
// import { BRAND } from "../css/brand";
// import { useNavigate, useLocation, NavLink } from "react-router-dom";
// import profileImage from "../asset/images/favi.jpg";
// import Sidebar from "../Dashboard/Sidebar";
// import Dashboards from "../Dashboard/Dashboards";
// import Entities from "../Dashboard/Entities";
// import Keys from "../Dashboard/Keys";
// import Inventory from "../Dashboard/Inventory";
// import Orders from "../Dashboard/Orders";
// import Payments from "../Dashboard/Payments";
// import Balances from "../Dashboard/Balance";
// import Support from "../Dashboard/Support";
// import Account from "../Dashboard/Account";
// import ActivationReport from "../Dashboard/ActivationReport";
// import UserViewPage from "../Dashboard/UserViewPage";
// import Enterprise from "../Dashboard/Enterprise";
// import EnterpriseUI from "../Dashboard/EnterpriseUI";
// import FormWizardTesting from "../Components/FormWizardTesting";
// import Devices from "../Dashboard/Devices";
// import CustomerCards from "../Components/CustomerCard";

// export default function MultiRolePanel() {
//   const [role, setRole] = useState(2);
//   const [businessName, setBusinessName] = useState("");
//   const [code, setCode] = useState("");
//   const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 640);
//   const [open, setOpen] = useState(false);
//   const menuRef = useRef(null);
//   const navigate = useNavigate();
//   const location = useLocation();

//   // ✅ Pages mapping
//   const pageComponents = {
//     dashboard: <Dashboards role={role} />,
//     entities: <Entities role={role} />,
//     keys: <Keys role={role} />,
//     inventory: <Inventory />,
//     report: <ActivationReport />,
//     customers: <CustomerCards />,
//     devices: <Devices/>,
//     orders: <Orders />,
//     payments: <Payments />,
//     balances: <Balances />,
//     support: <Support />,
//     account: <Account />,
//     enterprises: <EnterpriseUI/>,

//   };

//   const nav = [
//     { path: "/dashboard", icon: <LayoutDashboard size={18} />, label: "Dashboard" },
//     { path: "/dashboard/entities", icon: <Users size={18} />, label: "Entities" },
//     { path: "/dashboard/keys", icon: <KeyRound size={18} />, label: "Keys" },
//     { path: "/dashboard/inventory", icon: <Package size={18} />, label: "Inventory" },
//     { path: "/dashboard/report", icon: <FileTextIcon size={18} />, label: "Insights" },
//     { path: "/dashboard/customers", icon: <User2 size={18} />, label: "Customers" },
//     { path: "/dashboard/devices", icon: <Cpu size={18} />, label: "Devices" },
//     { path: "/dashboard/enterprises", icon: <Cloud size={18} />, label: "Enterprises" },
//     { path: "/dashboard/orders", icon: <ClipboardList size={18} />, label: "Orders" },
//     // { path: "/dashboard/payments", icon: <BarChart3 size={18} />, label: "Payments" },
//     // { path: "/dashboard/support", icon: <FileText size={18} />, label: "Support" },
//     { path: "/dashboard/account", icon: <Settings size={18} />, label: "Account" },
//   ];

//   // ✅ Session handling
//   useEffect(() => {
//     const stored = sessionStorage.getItem("authUserData1");
//     if (stored) {
//       try {
//         const user = JSON.parse(stored);
//         const u = user?.data?.[0];
//         if (u) {
//           setRole(u.role);
//           setBusinessName(u.business_name || u.owner_name || "User");
//           setCode(u.org_code || "-");
//         }
//       } catch (err) {
//         console.error("Invalid session data:", err);
//       }
//     } else {
//       navigate("/login");
//     }
//   }, [navigate]);

//   // ✅ Auto-close dropdown when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (menuRef.current && !menuRef.current.contains(event.target)) {
//         setOpen(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   // ✅ Handle logout & profile
//   const handleLogout = () => {
//     sessionStorage.removeItem("authUserData1");
//     navigate("/login");
//   };

//   const handleProfileClick = () => {
//     navigate("/dashboard/account");
//     setOpen(false);
//   };

//   // ✅ Layout
//   return (
//     <div className="min-h-screen bg-white text-slate-900">
//       {/* Header */}
//       <header className="sticky top-0 z-40 shadow-sm" style={{ backgroundColor: BRAND.darkBlue }}>
//         <div className="mx-auto px-3 sm:px-6 lg:px-8">
//           <div className="flex h-14 items-center justify-between">
//             {/* Left: Menu + Business */}
//             <div className="flex items-center gap-2">
//               <button
//                 onClick={() => setSidebarOpen((s) => !s)}
//                 className="hidden sm:inline-flex rounded-xl p-2 hover:bg-white/10 focus:outline-none"
//               >
//                 <Menu className="text-white" size={20} />
//               </button>
//               <span className="text-sm font-semibold tracking-wide text-white/80">
//                 {businessName}
//               </span>
//             </div>

//             {/* Right: Notification + Profile */}
//             <div className="flex items-center gap-2">
//               <button className="relative rounded-xl p-2 hover:bg-white/10">
//                 <Bell className="text-white" size={18} />
//                 <span
//                   className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full text-[10px]"
//                   style={{ backgroundColor: BRAND.red, color: "#FFF" }}
//                 >
//                   3
//                 </span>
//               </button>

//               <div className="relative" ref={menuRef}>
//                 <button
//                   onClick={() => setOpen(!open)}
//                   className="group flex items-center gap-2 rounded-xl bg-white/10 px-2 py-1.5 text-white hover:bg-white/20 transition"
//                 >
//                   <img
//                     src={profileImage}
//                     alt="Profile"
//                     className="h-6 w-6 rounded-full object-cover border border-white/30"
//                   />
//                   <span className="text-sm">{code}</span>
//                   <ChevronDown size={16} className="opacity-80" />
//                 </button>

//                 {open && (
//                   <div className="absolute right-0 mt-2 w-40 rounded-xl bg-white shadow-lg ring-1 ring-black/5 z-50">
//                     <ul className="py-1 text-sm text-gray-700">
//                       <li>
//                         <button
//                           onClick={handleProfileClick}
//                           className="flex w-full items-center gap-2 px-4 py-2 hover:bg-gray-100"
//                         >
//                           <User size={16} /> Profile
//                         </button>
//                       </li>
//                       <li>
//                         <button
//                           onClick={handleLogout}
//                           className="flex w-full items-center gap-2 px-4 py-2 hover:bg-gray-100 text-red-600"
//                         >
//                           <LogOut size={16} /> Logout
//                         </button>
//                       </li>
//                     </ul>
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>
//         </div>
//       </header>

//       {/* Main Layout */}
//       <div className="mx-auto px-3 sm:px-6 lg:px-8">
//         <div className="grid grid-cols-12 gap-3 sm:gap-4 py-3 sm:py-4">
//           {/* Sidebar (desktop) */}
//           {sidebarOpen && (
//             <aside className="hidden sm:block col-span-12 sm:col-span-4 lg:col-span-2">
//               <div className="sticky top-16 space-y-2 rounded-2xl p-2 bg-slate-50">
//                 {nav.map((n) => (
//                   <NavLink
//                     key={n.path}
//                     to={n.path}
//                     end={n.path === "/dashboard"}
//                     className={({ isActive }) =>
//                       `flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm ${
//                         isActive ? "bg-white font-semibold" : "hover:bg-white"
//                       }`
//                     }
//                   >
//                     <span className="text-slate-700">{n.icon}</span>
//                     <span className="text-slate-700">{n.label}</span>
//                   </NavLink>
//                 ))}
//               </div>
//             </aside>
//           )}

//           {/* Content */}
//           <main
//             className={
//               sidebarOpen ? "col-span-12 sm:col-span-8 lg:col-span-10" : "col-span-12"
//             }
//           >
//             {location.pathname.startsWith("/dashboard/entity/") ? (
//               <UserViewPage />
//             ) : location.pathname === "/dashboard/enterprises/create" ? (
//               <FormWizardTesting />
//             ) : (
//               pageComponents[location.pathname.split("/")[2] || "dashboard"]
//             )}
//           </main>
//         </div>
//       </div>

//       {/* Bottom Navigation (mobile) */}
//       <nav className="fixed bottom-0 left-0 right-0 z-50 overflow-x-auto bg-white border-t shadow sm:hidden">
//         <div className="flex w-max">
//           {nav.map((n) => (
//             <NavLink
//               key={n.path}
//               to={n.path}
//               end={n.path === "/dashboard"}
//               className={({ isActive }) =>
//                 `flex-shrink-0 w-1/4 flex flex-col items-center justify-center py-2 text-xs ${
//                   isActive ? "text-blue-600" : "text-slate-600"
//                 }`
//               }
//             >
//               {n.icon}
//               <span>{n.label}</span>
//             </NavLink>
//           ))}
//         </div>
//       </nav>

//       {/* Footer */}
//       <footer className="mt-4 sm:mt-6 border-t py-4 text-center text-xs text-slate-500">
//         © {new Date().getFullYear()} RR Technology — MultiRole Panel
//       </footer>
//     </div>
//   );
// }


// import React, { useEffect, useState, useRef } from "react";
// import { Menu, Bell, ChevronDown, LogOut, User } from "lucide-react";
// import { BRAND } from "../css/brand";
// import { LOGO_URL } from "../Data/LOGO_URL";
// import Sidebar from "../Dashboard/Sidebar";
// import Dashboards from "../Dashboard/Dashboards";
// import Entities from "../Dashboard/Entities";
// import Keys from "../Dashboard/Keys";
// import Inventory from "../Dashboard/Inventory";
// import Orders from "../Dashboard/Orders";
// import Payments from "../Dashboard/Payments";
// import Balances from "../Dashboard/Balance";
// import Support from "../Dashboard/Support";
// import Account from "../Dashboard/Account";
// import { useNavigate, useLocation } from "react-router-dom";
// import profileImage from "../asset/images/favi.jpg"
// import ActivationReport from "../Dashboard/ActivationReport";
// import UserViewPage from "../Dashboard/UserViewPage";

// export default function MultiRolePanel() {
//   const [role, setRole] = useState(2);
//   const [businessName, setBusinessName] = useState("");
//   const [Code, setCode] = useState("");
//   const [sidebarOpen, setSidebarOpen] = useState(false);

//   const [activePage, setActivePage] = useState("dashboard");
//   const navigate = useNavigate();
//   const location = useLocation();

//   const [open, setOpen] = useState(false);
//   const menuRef = useRef(null);

//   // ✅ Close dropdown when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (menuRef.current && !menuRef.current.contains(event.target)) {
//         setOpen(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   const handleLogout = () => {
//     sessionStorage.removeItem("authUserData1");
//     navigate("/login");
//   };

//   const handleProfileClick = () => {
//     navigate("/dashboard/account");
//     setOpen(false);
//   };

//   // ✅ Load user data
//   useEffect(() => {
//     const stored = sessionStorage.getItem("authUserData1");
//     if (stored) {
//       try {
//         const user = JSON.parse(stored);
//         if (user?.data?.[0]) {
//           setRole(user.data[0].role);
//           setBusinessName(
//             user.data[0].business_name ||
//               user.data[0].owner_name ||
//               "User"
//           );
//           setCode(user.data[0].org_code || "-")
//         }
//       } catch (err) {
//         console.error("Invalid session data:", err);
//       }
//     }
//   }, []);

//   // ✅ Sync activePage with URL (so refresh works properly)
//   useEffect(() => {
//     const parts = location.pathname.split("/");
//     const currentPage = parts[2] || "dashboard";
//     setActivePage(currentPage);
//   }, [location]);

//   // ✅ Page components mapping
//   const pageComponents = {
//     dashboard: <Dashboards role={role} />,
//     entities: <Entities role={role} />,
//     keys: <Keys role={role} />,
//     inventory: <Inventory />,
//     report: <ActivationReport />,
//     orders: <Orders />,
//     payments: <Payments />,
//     balances: <Balances />,
//     support: <Support />,
//     account: <Account />,

//   };

//   // ✅ Handle navigation
//   const handlePageChange = (key) => {
//     setActivePage(key);
//     navigate(`/dashboard/${key}`);
//     setSidebarOpen(false);
//   };

//   return (
//     <div className="min-h-screen bg-white text-slate-900">
//       {/* Header */}
//       <header
//         className="sticky top-0 z-40 shadow-sm px-2"
//         style={{ backgroundColor: BRAND.darkBlue }}
//       >
//         <div className="mx-auto px-2 sm:px-6 lg:px-8 ">
//           <div className="flex h-14 items-center justify-between">
//             {/* Left: Logo + Menu */}
//             <div className="flex items-center gap-2">
//               <button
//                 onClick={() => setSidebarOpen((s) => !s)}
//                 className="rounded-xl hover:bg-white/10"
//                 aria-label="Toggle sidebar"
//               >
//                 <Menu className="text-white" size={20} />
//               </button>
//               <div className="flex items-center">
//                 <img
//                   src={profileImage}
//                   alt="Logo"
//                   className="h-8 w-auto hidden"
//                 />
//                 <span className="ml-1 text-sm font-semibold text-white/80 md:inline">
//                   {businessName}
//                 </span>
//               </div>
//             </div>

//             {/* Right: Notifications + Profile */}
//             <div className="flex items-center gap-2">
//               <button className="relative rounded-xl p-2 hover:bg-white/10">
//                 <Bell className="text-white" size={18} />
//                 <span
//                   className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full text-[10px]"
//                   style={{ backgroundColor: BRAND.red, color: "#FFF" }}
//                 >
//                   3
//                 </span>
//               </button>
//               <div className="relative" ref={menuRef}>
//                 {/* Profile Button */}
//                 <button
//                   onClick={() => setOpen(!open)}
//                   className="group flex items-center gap-2 rounded-xl bg-white/10 px-2 py-1.5 text-white hover:bg-white/20 transition"
//                 >
//                  <img
//                     src={profileImage}
//                     alt="Profile"
//                     className="h-6 w-6 rounded-full object-cover border border-white/30"
//                   />
//                   <span className="text-sm">{Code}</span>
//                   <ChevronDown size={16} className="opacity-80" />
//                 </button>

//                 {/* Dropdown Menu */}
//                 {open && (
//                   <div className="absolute right-0 mt-2 w-40 rounded-xl bg-white shadow-lg ring-1 ring-black/5 z-50">
//                     <ul className="py-1 text-sm text-gray-700">
//                       <li>
//                         <button
//                           onClick={handleProfileClick}
//                           className="flex w-full items-center gap-2 px-4 py-2 hover:bg-gray-100"
//                         >
//                           <User size={16} /> Profile
//                         </button>
//                       </li>
//                       <li>
//                         <button
//                           onClick={handleLogout}
//                           className="flex w-full items-center gap-2 px-4 py-2 hover:bg-gray-100 text-red-600"
//                         >
//                           <LogOut size={16} /> Logout
//                         </button>
//                       </li>
//                     </ul>
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>
//         </div>
//       </header>

//       {/* Main Layout */}
//       <div className="flex flex-1 px-2">
//         {/* Sidebar (desktop) */}
//         {sidebarOpen && (
//           <aside className="hidden lg:block w-52 bg-white border-r shadow-sm">
//             <Sidebar activePage={activePage} onPageChange={handlePageChange} />
//           </aside>
//         )}
          

//         {/* Sidebar (mobile overlay) */}
//         {sidebarOpen && (
//           <>
//             <aside className="fixed inset-y-0 left-0 z-50 bg-white w-2/3 p-3 shadow-md lg:hidden">
//               <Sidebar
//                 activePage={activePage}
//                 onPageChange={handlePageChange}
//               />
//               <button
//                 onClick={() => setSidebarOpen(false)}
//                 className="absolute top-3 right-3 rounded-full p-2 bg-slate-200"
//               >
//                 Close
//               </button>
//             </aside>
//             <div
//               onClick={() => setSidebarOpen(false)}
//               className="fixed inset-0 z-40 bg-black/30 lg:hidden"
//             />
//           </>
//         )}

//         {/* Content */}
//         <main className="flex-1">
//           {location.pathname.startsWith("/dashboard/entity/") ? (
//             <UserViewPage />
//           ) : (
//             pageComponents[activePage] || (
//               <div className="text-center text-slate-500">Page not found</div>
//             )
//           )}
//         </main>

//       </div>

//       {/* Footer */}
//       <footer className="border-t py-4 text-center text-xs text-slate-500">
//         © {new Date().getFullYear()} RR Technology
//       </footer>
//     </div>
//   );
// }



// import React, { useEffect, useState } from "react";
// import { Menu, Bell, ChevronDown } from "lucide-react";
// import { BRAND } from "../css/brand";
// import { LOGO_URL } from "../Data/LOGO_URL";
// import Sidebar from "../Dashboard/Sidebar";
// import RoleRouter from "../Routes/RoleRouter";
// import Dashboards from "../Dashboard/Dashboards";
// import Entities from "../Dashboard/Entities";
// import Keys from "../Dashboard/Keys";
// import Inventory from "../Dashboard/Inventory";
// import Orders from "../Dashboard/Orders";
// import Payments from "../Dashboard/Payments";
// import Balances from "../Dashboard/Balance";
// import Support from "../Dashboard/Support";
// import Account from "../Dashboard/Account";
// import { useNavigate, useLocation } from "react-router-dom"; // ✅ added useLocation

// export default function MultiRolePanel() {
//   const [role, setRole] = useState(2);
//   const [businessName, setBusinessName] = useState("");
//   const [sidebarOpen, setSidebarOpen] = useState(false);
//   const [activePage, setActivePage] = useState("dashboard");
//   const navigate = useNavigate();
//   const location = useLocation(); // ✅ get current route info

//   // Load user data
//   useEffect(() => {
//     const stored = sessionStorage.getItem("authUserData1");
//     if (stored) {
//       try {
//         const user = JSON.parse(stored);
//         if (user?.data?.[0]) {
//           setRole(user.data[0].role);
//           setBusinessName(user.data[0].business_name || user.data[0].owner_name || "User");
//         }
//       } catch (err) {
//         console.error("Invalid session data:", err);
//       }
//     }
//   }, []);

//   // ✅ Sync activePage with URL (so refresh works properly)
//   useEffect(() => {
//     // Example: /dashboard/keys → ["", "dashboard", "keys"]
//     const parts = location.pathname.split("/");
//     const currentPage = parts[2] || "dashboard"; // adjust based on your route
//     setActivePage(currentPage);
//   }, [location]);

//   const pageComponents = {
//     dashboard: <Dashboards role={role} />,
//     entities: <Entities role={role} />,
//     keys: <Keys role={role} />,
//     inventory: <Inventory />,
//     orders: <Orders />,
//     payments: <Payments />,
//     balances: <Balances />,
//     support: <Support />,
//     account: <Account />,
//   };

//   const handlePageChange = (key) => {
//     setActivePage(key);
//     navigate(`/dashboard/${key}`);
//     setSidebarOpen(false);
//   };

//   return (
//     <div className="min-h-screen bg-white text-slate-900">
//       {/* Header */}
//       <header className="sticky top-0 z-40 shadow-sm" style={{ backgroundColor: BRAND.darkBlue }}>
//         <div className="mx-auto  px-3 sm:px-6 lg:px-8">
//           <div className="flex h-14 items-center justify-between">
//             <div className="flex items-center gap-2">
//               <button
//                 onClick={() => setSidebarOpen((s) => !s)}
//                 className="rounded-xl p-2 hover:bg-white/10"
//                 aria-label="Toggle sidebar"
//               >
//                 <Menu className="text-white" size={20} />
//               </button>
//               <img src={LOGO_URL} alt="Logo" className="h-8 w-auto" />
//               <span className="ml-1 text-sm font-semibold text-white/80">{businessName}</span>
//             </div>

//             <div className="flex items-center gap-2">
//               <button className="relative rounded-xl p-2 hover:bg-white/10">
//                 <Bell className="text-white" size={18} />
//                 <span
//                   className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full text-[10px]"
//                   style={{ backgroundColor: BRAND.red, color: "#FFF" }}
//                 >
//                   3
//                 </span>
//               </button>
//               <button className="group flex items-center gap-2 rounded-xl bg-white/10 px-2 py-1.5 text-white hover:bg-white/20">
//                 <div className="h-6 w-6 overflow-hidden rounded-full bg-white/20" />
//                 <span className="text-sm">Profile</span>
//                 <ChevronDown size={16} className="opacity-80" />
//               </button>
//             </div>
//           </div>
//         </div>
//       </header>

//       {/* Main Layout */}
//       <div className="mx-auto  px-3 sm:px-6 lg:px-8">
//         <div className="grid grid-cols-12 gap-3 sm:gap-4 py-3 sm:py-4">
//           {/* Sidebar */}
//           {sidebarOpen && (
//           <aside className="hidden lg:block col-span-2">
//             <Sidebar activePage={activePage} onPageChange={handlePageChange} />
//           </aside>
//           )}
//           {/* Sidebar (mobile overlay) */}
//           {sidebarOpen && (
//             <>
//               <aside className="fixed inset-y-0 left-0 z-50 bg-white lg:hidden w-2/3 p-3 shadow-md">
//                 <Sidebar activePage={activePage} onPageChange={handlePageChange} />
//                 <button
//                   onClick={() => setSidebarOpen(false)}
//                   className="absolute top-3 right-3 rounded-full p-2 bg-slate-200"
//                 >
//                   Close
//                 </button>
//               </aside>
//               <div
//                 onClick={() => setSidebarOpen(false)}
//                 className="fixed inset-0 z-40 bg-black/30 lg:hidden"
//               />
//             </>
//           )}

//           {/* Content */}
           
//         </div>
//       </div>
//       <Dashboards role={role}/>
    
//       {/* Footer */}
//       <footer className="mt-4 sm:mt-6 border-t py-4 text-center text-xs text-slate-500">
//         © {new Date().getFullYear()} Goelectronix — Multi-Role Panel
//       </footer>
//     </div>
//   );
// }


// import React, { useEffect, useState } from "react";
// import { Menu, Bell, ChevronDown } from "lucide-react";
// import { BRAND } from "../css/brand";
// import { LOGO_URL } from "../Data/LOGO_URL";
// import Sidebar from "../Dashboard/Sidebar";
// import RoleRouter from "../Routes/RoleRouter"; // Only used for small screens
// import Dashboards from "../Dashboard/Dashboards";
// import Entities from "../Dashboard/Entities";
// import Keys from "../Dashboard/Keys";
// import Inventory from "../Dashboard/Inventory";
// import Orders from "../Dashboard/Orders";
// import Payments from "../Dashboard/Payments";
// import Balances from "../Dashboard/Balance";
// import Support from "../Dashboard/Support";
// import Account from "../Dashboard/Account";
// import { useNavigate } from "react-router-dom";

// export default function MultiRolePanel() {
//   const [role, setRole] = useState(2);
//   const [businessName, setBusinessName] = useState("");
//   const [sidebarOpen, setSidebarOpen] = useState(false);
//   const [activePage, setActivePage] = useState("dashboard"); // Large screen active page
//   const navigate = useNavigate();

//   useEffect(() => {
//     // Get user info from session
//     const stored = sessionStorage.getItem("authUserData1");
//     if (stored) {
//       try {
//         const user = JSON.parse(stored);
//         if (user?.data?.[0]) {
//           setRole(user.data[0].role);
//           setBusinessName(user.data[0].business_name || user.data[0].owner_name || "User");
//         }
//       } catch (err) {
//         console.error("Invalid session data:", err);
//       }
//     }
//   }, []);

//   const pageComponents = {
//     dashboard: <Dashboards role={role} />,
//     entities: <Entities role={role} />,
//     keys: <Keys role={role} />,
//     inventory: <Inventory />,
//     orders: <Orders />,
//     payments: <Payments />,
//     balances: <Balances />,
//     support: <Support />,
//     account: <Account />,
//   };

//   const handlePageChange = (key) => {
//     setActivePage(key);
//     navigate(`/dashboard/${key}`); // Update URL for small screens
//     setSidebarOpen(false); // Close sidebar on small screens
//   };

//   return (
//     <div className="min-h-screen bg-white text-slate-900">
//       {/* Top Bar */}
//       <header className="sticky top-0 z-40 shadow-sm" style={{ backgroundColor: BRAND.darkBlue }}>
//         <div className="mx-auto  px-3 sm:px-6 lg:px-8">
//           <div className="flex h-14 items-center justify-between">
//             <div className="flex items-center gap-2">
//               <button
//                 onClick={() => setSidebarOpen((s) => !s)}
//                 className="rounded-xl p-2 hover:bg-white/10"
//                 aria-label="Toggle sidebar"
//               >
//                 <Menu className="text-white" size={20} />
//               </button>
//               <img src={LOGO_URL} alt="Logo" className="h-8 w-auto" />
//               <span className="ml-1 text-sm font-semibold text-white/80">{businessName}</span>
//             </div>

//             <div className="flex items-center gap-2">
//               <button className="relative rounded-xl p-2 hover:bg-white/10">
//                 <Bell className="text-white" size={18} />
//                 <span
//                   className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full text-[10px]"
//                   style={{ backgroundColor: BRAND.red, color: "#FFF" }}
//                 >
//                   3
//                 </span>
//               </button>
//               <button className="group flex items-center gap-2 rounded-xl bg-white/10 px-2 py-1.5 text-white hover:bg-white/20">
//                 <div className="h-6 w-6 overflow-hidden rounded-full bg-white/20" />
//                 <span className="text-sm">Profile</span>
//                 <ChevronDown size={16} className="opacity-80" />
//               </button>
//             </div>
//           </div>
//         </div>
//       </header>

//       <div className="mx-auto  px-3 sm:px-6 lg:px-8">
//         <div className="grid grid-cols-12 gap-3 sm:gap-4 py-3 sm:py-4">
//           {/* Sidebar for large screens */}
//           <aside className="hidden lg:block col-span-2">
//             <Sidebar activePage={activePage} onPageChange={handlePageChange} />
//           </aside>

//           {/* Sidebar toggle for small screens */}
//           {sidebarOpen && (
//             <aside className="fixed inset-0 z-50 bg-white lg:hidden w-2/3 p-3 shadow-md">
//               <Sidebar activePage={activePage} onPageChange={handlePageChange} />
//               <button
//                 onClick={() => setSidebarOpen(false)}
//                 className="absolute top-3 right-3 rounded-full p-2 bg-slate-200"
//               >
//                 Close
//               </button>
//             </aside>
//           )}
//           {sidebarOpen && <div onClick={() => setSidebarOpen(false)} className="fixed inset-0 z-40 bg-black/30 lg:hidden" />}

//           {/* Main content */}
//           <main className="col-span-12 lg:col-span-10">
//             {/* Large screens: show component directly */}
//             <div className="hidden lg:block">{pageComponents[activePage]}</div>

//             {/* Small screens: routed content via RoleRouter */}
//             <div className="lg:hidden">
//               <RoleRouter role={role} />
//             </div>
//           </main>
//         </div>
//       </div>

//       <footer className="mt-4 sm:mt-6 border-t py-4 text-center text-xs text-slate-500">
//         © {new Date().getFullYear()} Goelectronix — Multi-Role Panel
//       </footer>
//     </div>
//   );
// }



// import React, { useEffect, useState } from "react";
// import { Menu, Bell, ChevronDown } from "lucide-react";
// import { BRAND } from "../css/brand";
// import { LOGO_URL } from "../Data/LOGO_URL";
// import { getRoleName } from "../Data/ROLES";
// import Sidebar from "../Dashboard/Sidebar";
// import RoleRouter from "../Routes/RoleRouter";
// import runTests from "../Data/runTests";
// import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

// export default function MultiRolePanel() {
//   const [role, setRole] = useState(2);
//   console.log("role from session: ", role)
//   const [businessName, setBusinessName] = useState(""); // 🟢 Added
//   const [sidebarOpen, setSidebarOpen] = useState(false);

//   useEffect(() => {
//     runTests();

//     // ✅ Read auth user data from session
//     const stored = sessionStorage.getItem("authUserData1");
//     if (stored) {
//       try {
//         const user = JSON.parse(stored);
//         if (user?.data?.[0]) {
//           const numericRole = user.data[0].role;
//           const name = user.data[0].business_name || user.data[0].owner_name || "User";

//           setRole(numericRole); // 🔹 Convert numeric role
//           setBusinessName(name); // 🔹 Store business name
//         }
//         console.log("Session data:", user?.data[0]);
//       } catch (err) {
//         console.error("Invalid session data:", err);
//       }
//     }
//   }, []);

//   return (
//     <div className="min-h-screen bg-white text-slate-900">
//       {/* 🔷 Top Bar */}
//       <header
//         className="sticky top-0 z-40 shadow-sm"
//         style={{ backgroundColor: BRAND.darkBlue }}
//       >
//         <div className="mx-auto  px-3 sm:px-6 lg:px-8">
//           <div className="flex h-14 items-center justify-between">
//             <div className="flex items-center gap-2">
//               <button
//                 onClick={() => setSidebarOpen((s) => !s)}
//                 className="rounded-xl p-2 hover:bg-white/10"
//                 aria-label="Toggle sidebar"
//               >
//                 <Menu className="text-white" size={20} />
//               </button>
//               <img src={LOGO_URL} alt="Logo" className="h-8 w-auto" />
              
//               {/* 🟢 Show business name from session */}
//               <span className="ml-1 text-sm font-semibold text-white/80">
//                 {businessName}
//               </span>
//             </div>

//             <div className="flex items-center gap-2">
//               <button className="relative rounded-xl p-2 hover:bg-white/10">
//                 <Bell className="text-white" size={18} />
//                 <span
//                   className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full text-[10px]"
//                   style={{ backgroundColor: BRAND.red, color: "#FFF" }}
//                 >
//                   3
//                 </span>
//               </button>
//               <button className="group flex items-center gap-2 rounded-xl bg-white/10 px-2 py-1.5 text-white hover:bg-white/20">
//                 <div className="h-6 w-6 overflow-hidden rounded-full bg-white/20" />
//                 <span className="text-sm">Profile</span>
//                 <ChevronDown size={16} className="opacity-80" />
//               </button>
//             </div>
//           </div>
//         </div>
//       </header>

//       {/* 🔷 Main Content */}
//       <div className="mx-auto  px-3 sm:px-6 lg:px-8">
//         <div className="grid grid-cols-12 gap-3 sm:gap-4 py-3 sm:py-4">

//   {/* Sidebar for large screens (always visible) */}
//   {sidebarOpen && (
//   <aside className="hidden lg:block col-span-2">
//     <Sidebar />
//   </aside>
//  )}
//   {/* Sidebar / RoleRouter for small screens (toggleable) */}
//   {sidebarOpen && (
//     <aside className="fixed inset-0 z-50 bg-white lg:hidden w-2/3 p-3 shadow-md">
//       <Sidebar />
//       <button
//         onClick={() => setSidebarOpen(false)}
//         className="absolute top-3 right-3 rounded-full p-2 bg-slate-200"
//       >
//         Close
//       </button>
//     </aside>
//   )}

//   {/* Overlay for small screens */}
//   {sidebarOpen && (
//     <div
//       onClick={() => setSidebarOpen(false)}
//       className="fixed inset-0 z-40 bg-black/30 lg:hidden"
//     />
//   )}

//   {/* Main content for small screens only */}
//   <main className="col-span-12 lg:hidden">
//     {!sidebarOpen && <RoleRouter role={role} />}
//   </main>

// </div>



//       </div>

//       {/* 🔷 Footer */}
//       <footer className="mt-4 sm:mt-6 border-t py-4 text-center text-xs text-slate-500">
//         © {new Date().getFullYear()} Goelectronix — Multi-Role Panel
//       </footer>
//     </div>
//   );
// }



// *****************************20-10-2025 PHASE 1****************************************

// import React, { useEffect, useState } from "react";
// import { Menu, Bell, ChevronDown } from "lucide-react";
// import { BRAND } from "../css/brand";
// import { LOGO_URL } from "../Data/LOGO_URL";
// import { ROLES } from "../Data/ROLES";
// import Sidebar from "../Dashboard/Sidebar";
// import RoleRouter from "../Routes/RoleRouter";
// import runTests from "../Data/runTests";

// export default function MultiRolePanel() {
//   const [role, setRole] = useState("Company");
//   const [sidebarOpen, setSidebarOpen] = useState(false);

//   useEffect(() => { runTests(); }, []);

//   return (
//     <div className="min-h-screen bg-white text-slate-900">
//       {/* Top Bar */}
//       <header className="sticky top-0 z-40 shadow-sm" style={{ backgroundColor: BRAND.darkBlue }}>
//         <div className="mx-auto  px-3 sm:px-6 lg:px-8">
//           <div className="flex h-14 items-center justify-between">
//             <div className="flex items-center gap-2">
//               <button onClick={() => setSidebarOpen(s => !s)} className="rounded-xl p-2 hover:bg-white/10" aria-label="Toggle sidebar">
//                 <Menu className="text-white" size={20} />
//               </button>
//               <img src={LOGO_URL} alt="Logo" className="h-8 w-auto" />
//               <span className="ml-1 text-sm font-semibold text-white/80">{role} Panel</span>
//             </div>
//             <div className="flex items-center gap-2">
//               <select value={role} onChange={e => setRole(e.target.value)} className="rounded-xl bg-white/90 px-2 py-1 text-xs font-medium">
//                 {ROLES.map(r => (<option key={r}>{r}</option>))}
//               </select>
//               <button className="relative rounded-xl p-2 hover:bg-white/10">
//                 <Bell className="text-white" size={18} />
//                 <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full text-[10px]"
//                   style={{ backgroundColor: BRAND.red, color: "#FFF" }}>3</span>
//               </button>
//               <button className="group flex items-center gap-2 rounded-xl bg-white/10 px-2 py-1.5 text-white hover:bg-white/20">
//                 <div className="h-6 w-6 overflow-hidden rounded-full bg-white/20" />
//                 <span className="text-sm">Profile</span>
//                 <ChevronDown size={16} className="opacity-80" />
//               </button>
//             </div>
//           </div>
//         </div>
//       </header>

//       <div className="mx-auto  px-3 sm:px-6 lg:px-8">
//         <div className="grid grid-cols-12 gap-3 sm:gap-4 py-3 sm:py-4">
//           {sidebarOpen && <aside className="col-span-12 sm:col-span-4 lg:col-span-2">
//             <Sidebar />
//           </aside>}
//           <main className={sidebarOpen ? "col-span-12 sm:col-span-8 lg:col-span-10" : "col-span-12"}>
//             <RoleRouter role={role} />
//           </main>
//         </div>
//       </div>

//       <footer className="mt-4 sm:mt-6 border-t py-4 text-center text-xs text-slate-500">
//         © {new Date().getFullYear()} Goelectronix — Multi-Role Panel
//       </footer>
//     </div>
//   );
// }
